"use client";

import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { useLocale } from "../../runtime/RendererRuntime";

/**
 * Recharts-powered sparkline used by `MarketsBoardRenderer`. Sits in its own
 * client-component file so the chart only ships JS where it's actually
 * rendered.
 *
 * `compact` mode (used at lg+ where the chart sits next to the featured rate)
 * hides axes for a true sparkline look. Otherwise the chart shows a small Y
 * axis with min/max labels and an optional series of X-axis labels.
 *
 * Sizing is driven entirely by the parent's CSS (Tailwind classes on the
 * wrapper). Recharts' `ResponsiveContainer` reads the available box.
 */
export function MarketsSparkline({
	values,
	labels,
	stroke = "#00A356",
	fill = "#00A356",
	className = "block h-full w-full",
	compact = false,
}: {
	values?: number[];
	/** Optional X-axis labels (one per value). When omitted, X is hidden. */
	labels?: string[];
	stroke?: string;
	fill?: string;
	className?: string;
	/** When true, render as a true minimal sparkline — no axes, no grid. */
	compact?: boolean;
}) {
	const locale = useLocale();
	if (!values || values.length < 2) return null;

	const data = values.map((v, i) => ({ i, v, label: labels?.[i] }));
	const gradientId = `spark-${stroke.replace(/[^a-z0-9]/gi, "")}`;

	// Lock the Y domain to the data's actual min/max with a small padding so
	// the variation uses the full box. Recharts' default [0, dataMax] would
	// flatten series like FX rates whose absolute value (≈2300) dwarfs their
	// range (≈3).
	const min = Math.min(...values);
	const max = Math.max(...values);
	const range = max - min;
	const pad = range > 0 ? range * 0.15 : Math.abs(max) * 0.01 || 1;

	// Axis/tooltip numbers follow the site locale ("fr"→"fr-FR", "en"→"en-GB",
	// other codes pass through); an invalid tag falls back to fr-FR.
	const fmt = (n: number) => {
		const tag = locale === "fr" ? "fr-FR" : locale === "en" ? "en-GB" : locale;
		try {
			return n.toLocaleString(tag, { maximumFractionDigits: range < 1 ? 4 : 2 });
		} catch {
			return n.toLocaleString("fr-FR", { maximumFractionDigits: range < 1 ? 4 : 2 });
		}
	};

	return (
		<div className={className}>
			<ResponsiveContainer width="100%" height="100%">
				<AreaChart
					data={data}
					margin={
						compact
							? { top: 4, right: 4, left: 4, bottom: 4 }
							: { top: 8, right: 8, left: 0, bottom: labels ? 4 : 0 }
					}
				>
					<defs>
						<linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
							<stop offset="0%" stopColor={fill} stopOpacity={0.35} />
							<stop offset="100%" stopColor={fill} stopOpacity={0.02} />
						</linearGradient>
					</defs>
					{compact ? null : (
						<CartesianGrid stroke="#121212" strokeOpacity={0.06} vertical={false} />
					)}
					<YAxis
						hide={compact}
						domain={[min - pad, max + pad]}
						width={64}
						tick={{ fill: "#6B7280", fontSize: 11, dx: -2 }}
						tickLine={false}
						axisLine={false}
						tickFormatter={fmt}
						tickCount={3}
					/>
					{compact || !labels ? null : (
						<XAxis
							dataKey="label"
							tick={{ fill: "#6B7280", fontSize: 11 }}
							tickLine={false}
							axisLine={false}
							interval="preserveStartEnd"
							minTickGap={28}
						/>
					)}
					<Tooltip
						cursor={{ stroke: stroke, strokeOpacity: 0.4, strokeDasharray: "2 2" }}
						content={({ active, payload }) => {
							if (!active || !payload?.length) return null;
							const v = payload[0].value as number;
							const lbl = payload[0].payload?.label as string | undefined;
							return (
								<div className="rounded-sm bg-white/95 px-2 py-1 text-[11px] font-semibold text-[#00B862] shadow ring-1 ring-slate-200">
									{typeof v === "number" ? fmt(v) : v}
									{lbl ? <span className="ml-1.5 font-normal text-slate-500">· {lbl}</span> : null}
								</div>
							);
						}}
					/>
					<Area
						// `linear` draws straight segments between points so the
						// chart faithfully reflects each data point — `monotone`
						// smoothing was hiding small day-to-day moves that the
						// FX cards specifically want to surface.
						type="linear"
						dataKey="v"
						stroke={stroke}
						strokeWidth={2}
						fill={`url(#${gradientId})`}
						isAnimationActive={false}
						dot={false}
						activeDot={{ r: 3, stroke: stroke, strokeWidth: 1.5, fill: "#fff" }}
					/>
				</AreaChart>
			</ResponsiveContainer>
		</div>
	);
}
